import { FC } from "react";
import {
  Button,
  Center,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconArrowBack,
  IconBallFootball,
  IconCheck,
  IconTrophy,
  IconX,
} from "@tabler/icons-react";
import { useShootoutMutations } from "../../../../hooks/useShootoutMutations";
import { useShootoutDisplay } from "../../../../hooks/useShootoutDisplay";
import { components } from "../../../../openapi/generated-types";
import { TeamName } from "../../../../Components/TeamName";

type Game = components["schemas"]["Game"];

interface ShootOutSectionProps {
  game: Game;
}

interface ShotIndicatorProps {
  success: boolean | null; // null = not yet shot
}

const ShotIndicator: FC<ShotIndicatorProps> = ({ success }) => {
  if (success === null) {
    return <IconBallFootball size={28} color="#ddd" style={{ opacity: 0.4 }} />;
  }
  return <IconBallFootball size={28} color={success ? "#40c057" : "#fa5252"} />;
};

interface TeamColumnProps {
  teamId: string;
  shots: (boolean | null)[];
  score: number;
  isWinner: boolean;
  isFinished: boolean;
  isLoading: boolean;
  isActive: boolean;
  onShot: (success: boolean) => void;
}

const TeamColumn: FC<TeamColumnProps> = ({
  teamId,
  shots,
  score,
  isWinner,
  isFinished,
  isLoading,
  isActive,
  onShot,
}) => {
  return (
    <Paper
      p="md"
      radius="md"
      style={{
        backgroundColor: isWinner
          ? "rgba(64, 192, 87, 0.1)"
          : isActive
            ? "rgba(34, 139, 230, 0.05)"
            : undefined,
        border: isWinner
          ? "2px solid #40c057"
          : isActive
            ? "2px solid #228be6"
            : "1px solid #dee2e6",
      }}
    >
      <Stack gap="md" align="center">
        {/* Team Name & Score */}
        <Group gap="xs">
          {isWinner && <IconTrophy size={20} color="#40c057" />}
          <Text fw={700} size="lg">
            <TeamName teamId={teamId} />
          </Text>
        </Group>

        <Text size="2rem" fw={900} c={isWinner ? "green" : undefined}>
          {score}
        </Text>

        {/* Shot Indicators */}
        <Group gap={4} justify="center">
          {shots.map((success, i) => (
            <ShotIndicator key={i} success={success} />
          ))}
        </Group>

        {/* Action Buttons */}
        {!isFinished && (
          <SimpleGrid cols={2} spacing="xs" style={{ width: "100%" }}>
            <Button
              color="green"
              size="lg"
              disabled={isLoading || !isActive}
              onClick={() => {
                onShot(true);
              }}
              leftSection={<IconCheck size={20} />}
            >
              Tor
            </Button>
            <Button
              color="red"
              size="lg"
              disabled={isLoading || !isActive}
              onClick={() => {
                onShot(false);
              }}
              leftSection={<IconX size={20} />}
            >
              Daneben
            </Button>
          </SimpleGrid>
        )}
      </Stack>
    </Paper>
  );
};

export const ShootOutSection: FC<ShootOutSectionProps> = ({ game }) => {
  const { trackShot, revokeLastShot, finishShootout, isLoading } =
    useShootoutMutations(game.id);
  const shootout = useShootoutDisplay(game.id);

  const homeTeamId = game.actualTeams.homeTeamId ?? "";
  const guestTeamId = game.actualTeams.guestTeamId ?? "";
  const isFinished = game.shootOutFinished;
  const hasShots = game.shootOutShots.length > 0;
  const canFinish = game.shootOutWinnerDetermined && !isFinished;

  // Calculate scores
  const homeScore = game.shootOutShots.filter(
    (shot) => shot.teamId === homeTeamId && shot.success,
  ).length;
  const guestScore = game.shootOutShots.filter(
    (shot) => shot.teamId === guestTeamId && shot.success,
  ).length;

  // Get shot arrays for display
  const homeShots = shootout?.homeShots ?? [];
  const guestShots = shootout?.guestShots ?? [];

  const homeIsWinner = isFinished && game.shootOutWinnerId === homeTeamId;
  const guestIsWinner = isFinished && game.shootOutWinnerId === guestTeamId;

  // Determine which team is active (can shoot next)
  // If winner is determined: no one can shoot
  // Before first shot: both teams can shoot (free choice who starts)
  // After that: alternating
  const getActiveTeam = (): "home" | "guest" | "both" | "none" => {
    if (game.shootOutWinnerDetermined) return "none";
    if (game.shootOutShots.length === 0) return "both";
    const lastShot = game.shootOutShots[game.shootOutShots.length - 1];
    if (!lastShot) return "both";
    // The team that did NOT shoot last is now active
    return lastShot.teamId === homeTeamId ? "guest" : "home";
  };

  const activeTeam = getActiveTeam();
  const homeIsActive = activeTeam === "both" || activeTeam === "home";
  const guestIsActive = activeTeam === "both" || activeTeam === "guest";

  const handleRevoke = () => {
    void revokeLastShot();
  };

  const handleFinish = () => {
    void finishShootout();
  };

  return (
    <Stack gap="lg">
      {/* Header */}
      <Center>
        <Title order={2}>9-m-Schießen</Title>
      </Center>

      {/* Teams Display */}
      <SimpleGrid cols={2} spacing="md">
        <TeamColumn
          teamId={homeTeamId}
          shots={homeShots}
          score={homeScore}
          isWinner={homeIsWinner}
          isFinished={isFinished}
          isLoading={isLoading}
          isActive={homeIsActive}
          onShot={(success) => void trackShot(homeTeamId, success)}
        />
        <TeamColumn
          teamId={guestTeamId}
          shots={guestShots}
          score={guestScore}
          isWinner={guestIsWinner}
          isFinished={isFinished}
          isLoading={isLoading}
          isActive={guestIsActive}
          onShot={(success) => void trackShot(guestTeamId, success)}
        />
      </SimpleGrid>

      {/* Revoke Button */}
      {!isFinished && (
        <Center>
          <Button
            variant="subtle"
            color="gray"
            size="sm"
            disabled={!hasShots || isLoading}
            onClick={handleRevoke}
            leftSection={<IconArrowBack size={16} />}
          >
            Letzten Schuss rückgängig machen
          </Button>
        </Center>
      )}

      {/* Finish Button */}
      {canFinish && (
        <>
          <Divider />
          <Center>
            <Button
              size="lg"
              color="green"
              onClick={handleFinish}
              loading={isLoading}
              leftSection={<IconTrophy size={20} />}
            >
              Shootout abschließen
            </Button>
          </Center>
        </>
      )}
    </Stack>
  );
};
